import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { AutoLinkPlugin } from "@lexical/react/LexicalAutoLinkPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isTextNode,
  DOMConversionMap,
  DOMExportOutput,
  DOMExportOutputMap,
  ElementNode,
  isHTMLElement,
  Klass,
  LexicalEditor,
  LexicalNode,
  ParagraphNode,
  TextNode,
} from "lexical";
// import {MarkdownShortcutPlugin} from '@lexical/react/LexicalMarkdownShortcutPlugin';
// import {LinkPlugin} from '@lexical/react/LexicalLinkPlugin';
// import {ListPlugin} from '@lexical/react/LexicalListPlugin';
// import {HeadingNode, QuoteNode} from '@lexical/rich-text';
// import {ListNode, ListItemNode} from '@lexical/list';
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { richTextEditorStyles } from "./richtextTheme";
import ToolbarPlugin from "./plugins/toolbar.comp";
import { parseAllowedColor, parseAllowedFontSize } from "./styleConfig";
import { useFormContext, UseFormRegister, useWatch } from "react-hook-form";
import { useCallback, useEffect, useRef } from "react";
import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ImageNode } from "./nodes/ImageNode";
import { ImagePlugin } from "./plugins/imagePlugin";
import { YouTubeNode } from "./nodes/youtubeNode";
import { PasteYouTubePlugin } from "./plugins/youtubePlugin";
import { $generateNodesFromDOM } from "@lexical/html";
import { EditingMode } from "../reuseables/form.comp";
import { EditorMetaContext } from "./hooks/useMeta";

const placeholder = "Enter some rich text...";
const URL_MATCHER = /https?:\/\/[^\s]+/g;

const matchers = [
  (text: string) => {
    const match = text.match(URL_MATCHER);
    if (match === null) return null;
    return {
      index: match.index ?? 0,
      length: match[0].length,
      text: match[0],
      url: match[0],
    };
  },
];

const removeStylesExportDOM = (editor: LexicalEditor, target: LexicalNode): DOMExportOutput => {
  const output = target.exportDOM(editor);
  if (output && isHTMLElement(output.element)) {
    // Remove all inline styles and classes if the element is an HTMLElement
    // Children are checked as well since TextNode can be nested
    // in i, b, and strong tags.
    for (const el of [output.element, ...output.element.querySelectorAll("[style],[class]")]) {
      el.removeAttribute("class");
      el.removeAttribute("style");
    }
  }
  return output;
};

const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [ParagraphNode, removeStylesExportDOM],
  [TextNode, removeStylesExportDOM],
]);

const getExtraStyles = (element: HTMLElement): string => {
  // Parse styles from pasted input, but only if they match exactly the
  // sort of styles that would be produced by exportDOM
  let extraStyles = "";
  const fontSize = parseAllowedFontSize(element.style.fontSize);
  const backgroundColor = parseAllowedColor(element.style.backgroundColor);
  const color = parseAllowedColor(element.style.color);
  if (fontSize !== "" && fontSize !== "15px") {
    extraStyles += `font-size: ${fontSize};`;
  }
  if (backgroundColor !== "" && backgroundColor !== "rgb(255, 255, 255)") {
    extraStyles += `background-color: ${backgroundColor};`;
  }
  if (color !== "" && color !== "rgb(0, 0, 0)") {
    extraStyles += `color: ${color};`;
  }
  return extraStyles;
};

const constructImportMap = (): DOMConversionMap => {
  const importMap: DOMConversionMap = {};

  // Wrap all TextNode importers with a function that also imports
  // the custom styles implemented by the playground
  for (const [tag, fn] of Object.entries(TextNode.importDOM() || {})) {
    importMap[tag] = (importNode) => {
      const importer = fn(importNode);
      if (!importer) {
        return null;
      }
      return {
        ...importer,
        conversion: (element) => {
          const output = importer.conversion(element);
          if (output === null || output.forChild === undefined || output.after !== undefined || output.node !== null) {
            return output;
          }
          const extraStyles = getExtraStyles(element);
          if (extraStyles) {
            const { forChild } = output;
            return {
              ...output,
              forChild: (child, parent) => {
                const textNode = forChild(child, parent);
                if ($isTextNode(textNode)) {
                  textNode.setStyle(textNode.getStyle() + extraStyles);
                }
                return textNode;
              },
            };
          }
          return output;
        },
      };
    };
  }

  return importMap;
};

const editorConfig = {
  html: {
    export: exportMap,
    import: constructImportMap(),
  },
  namespace: "React.js Demo",
  nodes: [ParagraphNode, TextNode, ImageNode, YouTubeNode, AutoLinkNode, LinkNode],
  onError(error: Error) {
    throw error;
  },
  theme: richTextEditorStyles,
};

export function LoadInitialValuePlugin({ fieldName }: { fieldName: string }) {
  const [editor] = useLexicalComposerContext();
  const { watch } = useFormContext();
  const currentValue = watch(fieldName);
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (hasLoaded.current || !currentValue || currentValue === '""' || currentValue === "null") return;

    hasLoaded.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      let value = currentValue;

      try {
        const parsed = JSON.parse(currentValue);
        if (parsed && parsed.root) {
          const state = editor.parseEditorState(parsed);
          editor.setEditorState(state);
          return;
        }
      } catch {
        // Not JSON — continue
      }

      try {
        const parser = new DOMParser();
        const dom = parser.parseFromString(value, "text/html");
        const body = dom.body;

        if (body && body.childNodes.length > 0) {
          const nodes = $generateNodesFromDOM(editor, body);

          nodes.forEach((node) => {
            if (node instanceof ElementNode) {
              root.append(node);
            } else {
              const paragraph = $createParagraphNode();
              paragraph.append(node);
              root.append(paragraph);
            }
          });
          return;
        }
      } catch {
        // Not valid HTML — continue
      }

      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(String(value)));
      root.append(paragraph);
    });
  }, [currentValue, editor]);

  return null;
}

export function LexicalTextEditor({
  locale,
  fieldName,
  register,
  id,
}: {
  locale: string;
  fieldName: string;
  register: UseFormRegister<any>;
  id?: string;
}) {
  const { t, msg } = useTranslation();
  const { setValue, control } = useFormContext();

  // console.log("productIDlexc", id, "localelax", locale)

  useEffect(() => {
    register(fieldName);
  }, [register, fieldName]);

  const handleChange = useCallback(
    (editorState: any) => {
      editorState.read(() => {
        const json = editorState.toJSON();
        const serialized = JSON.stringify(json, null, 2);
        console.log(serialized);
        setValue(fieldName, serialized, { shouldDirty: true, shouldValidate: true });
      });
    },
    [setValue, fieldName]
  );

  return (
    <EditorMetaContext.Provider value={{ productId: id, locale: locale.replace("-", "") }}>
      <LexicalComposer initialConfig={editorConfig}>
        <div className="editor-container border rounded-lg dark:bg-her-backblue">
          <ToolbarPlugin />
          <div className={`editor-inner`}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={`editor-input overflow-scroll focus-visible:border-none w-full my-2 px-2 rounded-md mb-1 h-[300px] resize-none`}
                  aria-placeholder={placeholder}
                  placeholder={
                    <div className="editor-placeholder">{localeCore.getLocaleLabel(locale, t, msg) ?? "Enter Text Here"}</div>
                  }
                  data-role={fieldName}
                />
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <OnChangePlugin onChange={handleChange} />
            <LoadInitialValuePlugin fieldName={fieldName} />
            <PasteYouTubePlugin />
            <ImagePlugin />
            <AutoLinkPlugin matchers={matchers} />
            <LinkPlugin />
          </div>
        </div>
      </LexicalComposer>
    </EditorMetaContext.Provider>
  );
}
